import { useEffect, useRef } from "react";
import * as d3 from "d3";

const BCPieChart = ({ ...props }) => {
  const ref = useRef(null);
  const cache = useRef(props.data);
  const createPie = d3
    .pie()
    .value(d => d[props.valueName])
    .sort(null);
  const createArc = d3
    .arc()
    .innerRadius(props.innerRadius)
    .outerRadius(props.outerRadius);
  const colors = d3.scaleOrdinal(props.colorArray ? props.colorArray : d3.schemeCategory10);

  useEffect(
    () => {
      const data = createPie(props.data);
      const prevData = createPie(cache.current);
      const group = d3.select(ref.current);
      const groupWithData = group.selectAll("g.arc").data(data);

      groupWithData.exit().remove();

      const groupWithUpdate = groupWithData
        .enter()
        .append("g")
        .attr("class", "arc");

      const path = groupWithUpdate
        .append("path")
        .merge(groupWithData.select("path.arc"));

      const arcTween = (d, i) => {
        const interpolator = d3.interpolate(prevData[i], d);

        return t => createArc(interpolator(t));
      };

      const radius = Math.min(props.width, props.height) / 2;

      const arc = d3.arc()
        .innerRadius(radius * 0.5)         // This is the size of the donut hole
        .outerRadius(radius * 0.8);

      const outerArc = d3.arc()
        .innerRadius(radius * 0.9)
        .outerRadius(radius * 0.9);

      path
        .attr("class", "arc")
        .attr("fill", (d, i) => colors(i))
        .transition()
        .attrTween("d", arcTween);

      const polyline = groupWithUpdate
        .append("polyline")
        .merge(groupWithData.select("polyline"));

      if (props.labelName) {
        polyline.attr("stroke", "black")
          .style("fill", "none")
          .attr("stroke-width", 1)
          .attr("points", function (d) {
            const posA = arc.centroid(d); // line insertion in the slice
            const posB = outerArc.centroid(d); // line break: we use the other arc generator that has been built only for that
            const posC = outerArc.centroid(d); // Label position = almost the same as posB
            const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2; // we need the angle to see if the X position will be at the extreme right or extreme left
            posC[0] = radius * 0.95 * (midangle < Math.PI ? 1 : -1); // multiply by 1 or -1 to put it on the right or on the left
            return [posA, posB, posC];
          });

        const text = groupWithUpdate
          .append("text")
          .merge(groupWithData.select("text"));

        text
          .attr("text-anchor", function (d) {
            const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
            return (midangle < Math.PI ? "start" : "end");
          })
          .attr("alignment-baseline", "middle")
          .style("fill", "black")
          .style("font-size", 10)
          .transition()
          .attr("transform", function (d) {
            const pos = outerArc.centroid(d);
            const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
            pos[0] = radius * 0.99 * (midangle < Math.PI ? 1 : -1);
            return "translate(" + pos + ")";
          })
          .tween("text", (d, i, nodes) => {
            return () => (
              d3.select(nodes[i]).text(d.data[props.labelName])
            );
          });
      }

      cache.current = props.data;
    },
    [colors, createArc, createPie, props.height, props.width, props.data, props.labelName]
  );

  return (
    <svg viewBox={`0, 0, ${props.width + props.addedWidth}, ${props.height}`}>
      <g
        ref={ref}
        transform={`translate(${props.outerRadius + (props.addedWidth / 2)} ${props.outerRadius})`}
      />
    </svg>
  );
};

export default BCPieChart;
